import { Button, DatePicker, Input, message, Space, Spin, Table } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { serverFetch } from "@server";
import { fmtDate, strDate2Server } from "@extensions/convert";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./order.css";
import Order from "./order";

const STORAGE_KEY_FROM = "orders_dateFrom";
const STORAGE_KEY_TO = "orders_dateTo";

const loadSavedPeriod = () => {
  try {
    const from = localStorage.getItem(STORAGE_KEY_FROM);
    const to = localStorage.getItem(STORAGE_KEY_TO);
    if (from && to) {
      const dFrom = dayjs(from, "YYYY-MM-DD", true);
      const dTo = dayjs(to, "YYYY-MM-DD", true);
      if (dFrom.isValid() && dTo.isValid()) {
        return { dateFrom: dFrom, dateTo: dTo };
      }
    }
  } catch (e) {}
  return {
    dateFrom: dayjs().startOf("month"),
    dateTo: dayjs().endOf("month"),
  };
};

const Orders = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const saved = loadSavedPeriod();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(saved.dateFrom);
  const [dateTo, setDateTo] = useState(saved.dateTo);
  const [searchText, setSearchText] = useState("");
  const selectedOrderId = idParam != null ? parseInt(idParam, 10) || null : null;
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftDateFrom, setDraftDateFrom] = useState(null);
  const [draftDateTo, setDraftDateTo] = useState(null);

  useEffect(() => {
    if (dateFrom) {
      try {
        localStorage.setItem(STORAGE_KEY_FROM, dateFrom.format("YYYY-MM-DD"));
      } catch (e) {}
    }
    if (dateTo) {
      try {
        localStorage.setItem(STORAGE_KEY_TO, dateTo.format("YYYY-MM-DD"));
      } catch (e) {}
    }
  }, [dateFrom, dateTo]);

  const fetchOrders = (from, to) => {
    setLoading(true);
    const queryParams = {};
    if (from) queryParams.dateFrom = strDate2Server(from);
    if (to) queryParams.dateTo = strDate2Server(to);
    if (searchText.trim()) queryParams.search = searchText.trim();
    serverFetch("Order", { queryParams })
      .then(data => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || e.message || "Ошибка загрузки заказов");
      });
  };

  const getData = () => fetchOrders(dateFrom, dateTo);

  useEffect(() => {
    fetchOrders(saved.dateFrom, saved.dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- первичная загрузка при монтировании
  }, []);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (dateFrom && dateTo) {
      parts.push(`${dateFrom.format("DD.MM.YYYY")} — ${dateTo.format("DD.MM.YYYY")}`);
    } else if (dateFrom) parts.push(`с ${dateFrom.format("DD.MM.YYYY")}`);
    else if (dateTo) parts.push(`по ${dateTo.format("DD.MM.YYYY")}`);
    else parts.push("период не задан");
    const q = searchText.trim();
    if (q) parts.push(`поиск: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}`);
    return parts.join(" · ");
  }, [dateFrom, dateTo, searchText]);

  const openFilterModal = () => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (!draftDateFrom || !draftDateTo) {
      message.warning("Укажите период дат");
      return;
    }
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setFilterOpen(false);
    fetchOrders(draftDateFrom, draftDateTo);
  };

  const cancelFilter = () => setFilterOpen(false);

  const formatNum = v => (v != null && v !== "" ? Number(v) : null);
  const fmtMoney = v => {
    const n = formatNum(v);
    return n != null && !Number.isNaN(n) ? n : "—";
  };

  const fmtAreaWithUnit = v => {
    const s = fmtMoney(v);
    return s === "—" ? "—" : `${s} м²`;
  };

  /** Сумма: `1 234 567.89` — пробелы по разрядам, точка, два десятичных знака */
  const fmtSumAmount = v => {
    const n = formatNum(v);
    if (n == null || Number.isNaN(n)) return "—";
    const [intPart, frac] = n.toFixed(2).split(".");
    const neg = intPart.startsWith("-");
    const intDigits = neg ? intPart.slice(1) : intPart;
    const spaced = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${neg ? "-" : ""}${spaced}.${frac}`;
  };

  const buildOrderMobileLine1 = record => {
    const chunks = [];
    const client = record.predName?.trim();
    if (client) chunks.push(client);
    const addr = record.address?.trim();
    if (addr) chunks.push(`Адрес: ${addr}`);
    const phone = record.phone?.trim();
    if (phone) chunks.push(`т. ${phone}`);
    const text = chunks.join(" ");
    const company = record.companyName?.trim();
    return { text, company };
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 80 },
    { dataIndex: "docDate", title: "Дата", width: 110, render: fmtDate },
    { dataIndex: "companyName", title: "Компания", width: 160, ellipsis: true },
    { dataIndex: "docNum", title: "Номер док.", width: 100 },
    { dataIndex: "depNo", title: "Отдел", width: 80 },
    { dataIndex: "status", title: "Статус", width: 120 },
    { dataIndex: "predName", title: "Клиент", width: 200, ellipsis: true },
    { dataIndex: "address", title: "Адрес", ellipsis: true },
    { dataIndex: "area", title: "Площадь", width: 100, render: fmtAreaWithUnit },
    { dataIndex: "syma", title: "Сумма", width: 130, render: fmtSumAmount },
    { dataIndex: "debet", title: "Долг", width: 110, render: v => (v != null ? Number(v) : "—") },
    { dataIndex: "phone", title: "Телефон", width: 130 },
    { dataIndex: "finDate", title: "Дата гот.", width: 110, render: fmtDate },
  ];

  const tableEl = (
    <Table
      className="orders-list-table"
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="id"
      pagination={{ pageSize: 50 }}
      tableLayout="fixed"
      scroll={{ y: "calc(100vh - 200px)" }}
      size="small"
      onRow={record => ({
        onClick: () => navigate(`/orders/${record.id}`),
        style: { cursor: "pointer" },
      })}
    />
  );

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="orders-mobile-list">
        {items.map(record => {
          const { text: line1Text, company: line1Company } = buildOrderMobileLine1(record);
          const line1Empty = !line1Text && !line1Company;
          const docNumPart =
            record.docNum != null && record.docNum !== ""
              ? `N${record.docNum}`
              : "N—";
          const line3Parts = [`Площадь: ${fmtAreaWithUnit(record.area)}`];
          const debtN = formatNum(record.debet);
          const showDebt = debtN != null && !Number.isNaN(debtN) && debtN > 0;
          return (
            <div
              key={record.id}
              className="orders-mobile-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/orders/${record.id}`)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/orders/${record.id}`);
                }
              }}
            >
              <div className="orders-mobile-card-sum" aria-label={`Сумма ${fmtSumAmount(record.syma)}`}>
                {fmtSumAmount(record.syma)}
              </div>
              {showDebt ? (
                <div className="orders-mobile-card-debt" aria-label={`Долг ${fmtSumAmount(record.debet)}`}>
                  {fmtSumAmount(record.debet)}
                </div>
              ) : null}
              <div className="orders-mobile-card-line1">
                {line1Empty ? (
                  "—"
                ) : (
                  <>
                    {line1Text}
                    {line1Text && line1Company ? " " : null}
                    {line1Company ? (
                      <span className="orders-mobile-card-company-gray">{line1Company}</span>
                    ) : null}
                  </>
                )}
              </div>
              <div className="orders-mobile-card-line2">
                <span>{record.status ?? "—"}</span>
                <span className="orders-mobile-card-sep" aria-hidden>
                  ·
                </span>
                <span>{fmtDate(record.docDate) || "—"}</span>
                <span className="orders-mobile-card-sep" aria-hidden>
                  ·
                </span>
                <span>#{record.id}</span>
                <span className="orders-mobile-card-sep" aria-hidden>
                  ·
                </span>
                <span>
                  {docNumPart}/{record.depNo ?? "—"}
                </span>
              </div>
              <div
                className={
                  showDebt ? "orders-mobile-card-line3 orders-mobile-card-line3--with-debt" : "orders-mobile-card-line3"
                }
              >
                {line3Parts.join(" · ")}
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  return (
    <div className={isMobile ? "page orders-page--mobile" : "page orders-page-desktop"}>
      {isMobile ? (
        <>
          <div className="orders-mobile-toolbar">
            <div className="company-backups-mobile-search-row">
              <Input
                placeholder="Поиск"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && getData()}
                allowClear
                className="company-backups-mobile-search-input"
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={getData}
                loading={loading}
                disabled={!dateFrom || !dateTo}
                aria-label="Обновить"
                className="company-backups-mobile-refresh-btn"
              />
            </div>
            <div className="company-backups-mobile-filter-row">
              <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                {filterSummary}
              </span>
              <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                aria-label="Фильтр"
                className="company-backups-mobile-filter-icon-btn"
              />
            </div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">С даты</span>
                    <DatePicker
                      value={draftDateFrom}
                      onChange={setDraftDateFrom}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">По дату</span>
                    <DatePicker
                      value={draftDateTo}
                      onChange={setDraftDateTo}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="orders-mobile-scroll">{mobileListEl}</div>
        </>
      ) : (
        <>
          <Space className="orders-desktop-toolbar" style={{ marginBottom: 16 }} wrap>
            <span>Период с</span>
            <DatePicker value={dateFrom} onChange={setDateFrom} format="DD.MM.YYYY" allowClear={false} />
            <span>по</span>
            <DatePicker value={dateTo} onChange={setDateTo} format="DD.MM.YYYY" allowClear={false} />
            <Input
              placeholder="Поиск"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && getData()}
              style={{ width: 200 }}
              allowClear
            />
            <Button type="primary" onClick={getData} loading={loading}>
              Обновить
            </Button>
          </Space>
          <div className="orders-desktop-table-wrap">{tableEl}</div>
        </>
      )}
      {selectedOrderId != null && <Order orderId={selectedOrderId} onClose={() => navigate("/orders")} />}
    </div>
  );
};

export default Orders;
